import { inject } from '@angular/core';
import { GROUNDWORKS_DEFAULT_TENANT_ID, GroundworksAuthConfig } from '@groundworks/ngx-groundworks-auth';
import { ConfigService } from './config.service';
import { PostLoginService } from './services/post-login.service';

// Graph is deliberately left out of the interceptor: ProfileService sends its own User.Read token,
// which PostLoginService retries and re-authenticates at most once per session. An interceptor
// entry would replace that token, and its failures would redirect with no loop guard.
export function buildGroundworksAuthConfig(config: ConfigService): GroundworksAuthConfig {
  // The library fills a blank apiUrl or redirectUri with the page origin (for redirectUri, the
  // origin must then be a registered Entra reply URL), but logs that only at Info, below the
  // production threshold. The warning keeps a missing setting visible.
  warnIfBlank(config.authRedirectUrl, 'auth_redirect_url');
  warnIfBlank(config.apiUrl, 'api_url');

  return {
    clientId: config.clientId,
    tenantId: GROUNDWORKS_DEFAULT_TENANT_ID,
    redirectUri: config.authRedirectUrl.trim(),
    apiUrl: resolveApiUrl(config),
    // env.js values are untyped at runtime; Boolean() keeps the pre-migration truthiness semantics
    // (so a quoted 'false' counts as true) instead of failing the library's boolean check.
    production: Boolean(config.production),
    hooks: {
      // Runs in the app's injection context, after the auth APP_INITIALIZER: PostLoginService
      // depends (via ProfileService) on MsalService, so it must not be injected in the factory. A
      // rejection is logged by the library and passed to ErrorHandler.
      afterInteractionComplete: (account) => inject(PostLoginService).run(account),
    },
  };
}

/**
 * The API root for both the auth config and API_BASE_URL, so the SDK calls exactly the URL the
 * interceptor protects: trimmed, the page origin when blank (as the library would fill it), and
 * without trailing slashes (the library strips them from the protected key, gap G-06). Does not
 * warn; buildGroundworksAuthConfig reports a blank api_url once.
 */
export function resolveApiUrl(config: ConfigService): string {
  return (config.apiUrl.trim() || window.location.origin).replace(/\/+$/, '');
}

function warnIfBlank(url: string, setting: string): void {
  if (url.trim() === '') {
    console.warn(`[auth.config] ${setting} is empty; falling back to ${window.location.origin}.`);
  }
}
